import { useState, useEffect, useCallback, useMemo } from 'react';
import { InfinityScroll, SearchFilter } from 'app/components';
import { TFilterItems } from 'app/components/SearchFilter/SearchFilter';
import { useDebounce } from 'app/hooks';
import {
  ListBoxContainer,
  ListBoxContent,
  ListBoxSearchContainer,
  ListBoxWrapper,
} from './styles';

type IListBoxProps = {
  children: (item: unknown) => JSX.Element;
  onSearch?: (field: string, value: string) => void;
  items: Array<unknown>;
  isLoading?: boolean;
  handleRequest?: () => void;
  filterItems: TFilterItems[];
};

const ListBox = ({
  children,
  onSearch,
  items,
  isLoading = false,
  handleRequest,
  filterItems,
}: IListBoxProps) => {
  const [searchText, updateSearchText] = useState<string>('');
  const [selectedFilter, setSelectedFilter] = useState<TFilterItems>();
  const debouncedSearchTerm = useDebounce(searchText, 500);

  const selectedFilterParameter = useMemo(
    () =>
      selectedFilter?.parameterName || filterItems?.[0]?.parameterName || '',
    [selectedFilter?.parameterName, filterItems],
  );

  const handleSelectFilterItem = useCallback(
    (selectedFilterValue: TFilterItems) => {
      setSelectedFilter(selectedFilterValue);
    },
    [],
  );

  const handleSearch = useCallback(() => {
    if (typeof onSearch === 'function') {
      onSearch(selectedFilterParameter, searchText);
    }
  }, [onSearch, selectedFilterParameter, searchText]);

  const handleClearInput = useCallback(() => {
    updateSearchText('');
  }, []);

  const handleInputChange = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      updateSearchText(evt.target.value);
    },
    [],
  );

  useEffect(() => {
    if (debouncedSearchTerm !== undefined) {
      onSearch?.(selectedFilterParameter, debouncedSearchTerm);
    }
  }, [debouncedSearchTerm, selectedFilterParameter]);

  return (
    <ListBoxWrapper>
      <ListBoxSearchContainer>
        <SearchFilter
          inputValue={searchText}
          handleClearInput={handleClearInput}
          handleInputChange={handleInputChange}
          filterItems={filterItems}
          handleSelectFilterItem={handleSelectFilterItem}
          selectedFilter={selectedFilter?.name}
          handleSearch={handleSearch}
        />
      </ListBoxSearchContainer>
      <ListBoxContainer>
        <ListBoxContent>
          {items.map((item, index) => (
            <div key={index}>{children(item)}</div>
          ))}
          {handleRequest && (
            <InfinityScroll isLoading={isLoading} onRequest={handleRequest} />
          )}
        </ListBoxContent>
      </ListBoxContainer>
    </ListBoxWrapper>
  );
};

export default ListBox;
